// src/pages/DriverVerification.js
import React, { useState, useEffect } from "react";
import { collection, query, where, getDocs, doc, updateDoc, addDoc, serverTimestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebase";
import Paginator from "../components/Paginator";
import { formatDate, daysUntil } from "../utils/dateFormat";

const PAGE_SIZE = 10;

const STATUS_COLORS = {
  pending:       { bg: "rgba(234,179,8,0.1)",  border: "rgba(234,179,8,0.3)",  text: "#EAB308" },
  verified:      { bg: "rgba(34,197,94,0.1)",  border: "rgba(34,197,94,0.3)",  text: "#22C55E" },
  approved:      { bg: "rgba(34,197,94,0.1)",  border: "rgba(34,197,94,0.3)",  text: "#22C55E" },
  rejected:      { bg: "rgba(239,68,68,0.1)",  border: "rgba(239,68,68,0.3)",  text: "#EF4444" },
  expired:       { bg: "rgba(239,68,68,0.1)",  border: "rgba(239,68,68,0.3)",  text: "#EF4444" },
  expiring_soon: { bg: "rgba(234,179,8,0.1)",  border: "rgba(234,179,8,0.3)",  text: "#EAB308" },
  not_uploaded:  { bg: "rgba(85,85,85,0.1)",   border: "rgba(85,85,85,0.3)",   text: "var(--text-muted)" },
};

const STATUS_META = {
  pending:       { label: "Pending review", dot: "#EAB308", accent: "#EAB308" },
  verified:      { label: "Verified",       dot: "#22C55E", accent: "#22C55E" },
  approved:      { label: "Approved",        dot: "#22C55E", accent: "#22C55E" },
  rejected:      { label: "Rejected",        dot: "#EF4444", accent: "#EF4444" },
  expired:       { label: "Expired",         dot: "#EF4444", accent: "#EF4444" },
  expiring_soon: { label: "Expiring soon",   dot: "#EAB308", accent: "#EAB308" },
  not_uploaded:  { label: "Not uploaded",    dot: "#6B7280", accent: "#3A3A3A" },
};
const metaFor = (status) => STATUS_META[status] || STATUS_META.not_uploaded;

const Badge = ({ status }) => {
  const c = STATUS_COLORS[status] || STATUS_COLORS.not_uploaded;
  const m = metaFor(status);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: c.bg, border: `1px solid ${c.border}`, color: c.text, borderRadius: 20, padding: "3px 11px 3px 9px", fontSize: 11.5, fontWeight: 600, lineHeight: 1.4 }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: m.dot, flexShrink: 0 }} />
      {m.label}
    </span>
  );
};

// Tiny status pill for the header summary row (compact, no border).
const MiniStat = ({ icon, label, status }) => {
  const m = metaFor(status);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: "var(--text-secondary)", fontWeight: 500 }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: m.dot, flexShrink: 0 }} />
      <span style={{ opacity: 0.85 }}>{icon}</span>{label}
    </span>
  );
};

// Shown when a still-verified document has been auto-reopened for early renewal
// (by the expiry job, 30 days out). Tells the admin the driver can already
// re-upload and is still verified — so the Reopen button below is a stronger,
// status-dropping action, not a no-op.
const AutoReopenBadge = () => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.25)", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#3B82F6", marginBottom: 10 }}>
    <span>🔄</span>
    <span>Auto-opened for renewal — driver can upload a new copy now and stays verified until it expires.</span>
  </div>
);

const PhotoCol = ({ label, url, emptyText, borderColor = "var(--border)", subText }) => (
  <div>
    {label && <div style={{ color: "var(--text-secondary)", fontSize: 11, fontWeight: 600, marginBottom: 6 }}>{label}</div>}
    {url ? (
      <>
        <a href={url} target="_blank" rel="noreferrer">
          <img src={url} alt={label || ""} style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 10, border: `1.5px solid ${borderColor}`, display: "block", cursor: "pointer" }} />
        </a>
        <a href={url} target="_blank" rel="noreferrer" style={{ color: "#3B82F6", fontSize: 10.5, marginTop: 5, display: "inline-block", textDecoration: "none" }}>Open full size ↗</a>
        {subText && <div style={{ color: "var(--text-muted)", fontSize: 10, marginTop: 2 }}>{subText}</div>}
      </>
    ) : (
      <div style={{ height: 120, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)", borderRadius: 10, border: "1px dashed #2A2A2A" }}>
        <span style={{ color: "var(--text-muted)", fontSize: 12, textAlign: "center", padding: 8 }}>{emptyText}</span>
      </div>
    )}
  </div>
);

const TABS = [
  { key: "all_drivers", label: "All Drivers" },
  { key: "pending",     label: "Pending Drivers" },
  { key: "rejected",    label: "Rejected Drivers" },
  { key: "expiring",    label: "About to Expire" },
];

const POW_TYPE_LABELS = {
  passport: "Canadian Passport", pr_card: "PR Card",
  work_permit: "Work Permit",    birth_certificate: "Birth Certificate",
};

// A driver is fully verified only when ALL four documents are approved.
const isFullyVerified = (d) =>
  d.licenseStatus === "verified" &&
  d.profilePicStatus === "approved" &&
  d.proofOfWorkStatus === "verified" &&
  d.abstractStatus === "verified";

const BTN = {
  approve: { bg: "rgba(34,197,94,0.12)",  border: "rgba(34,197,94,0.35)",  color: "#22C55E" },
  reject:  { bg: "rgba(239,68,68,0.1)",   border: "rgba(239,68,68,0.3)",   color: "#EF4444" },
  neutral: { bg: "var(--surface2)",        border: "var(--border)",         color: "var(--text-secondary)" },
  purple:  { bg: "rgba(168,85,247,0.12)",  border: "rgba(168,85,247,0.4)",  color: "#A855F7" },
};
const btn = (variant, extra = {}) => ({
  display: "inline-flex", alignItems: "center", gap: 6,
  background: BTN[variant].bg, border: `1px solid ${BTN[variant].border}`,
  color: BTN[variant].color, borderRadius: 9, padding: "8px 15px",
  fontSize: 12.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
  transition: "filter 0.15s ease", ...extra,
});

// Collapsible section wrapper with a status accent bar on the left.
const Section = ({ title, badge, alertBadge, accent, isOpen, onToggle, children }) => (
  <div style={{ borderTop: "1px solid var(--surface2)" }}>
    <div
      onClick={onToggle}
      style={{ padding: "13px 20px 13px 15px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", userSelect: "none", position: "relative" }}
    >
      {/* accent bar */}
      <span style={{ position: "absolute", left: 15, top: 10, bottom: 10, width: 3, borderRadius: 3, background: accent || "transparent" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", paddingLeft: 16 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", letterSpacing: "-0.01em" }}>{title}</span>
        {badge}
        {alertBadge}
      </div>
      <span style={{ color: "var(--text-muted)", fontSize: 16, flexShrink: 0, marginLeft: 12, transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s ease", lineHeight: 1 }}>⌄</span>
    </div>
    {isOpen && <div style={{ padding: "0 20px 18px 31px" }}>{children}</div>}
  </div>
);

export default function DriverVerification() {
  const [allDrivers,       setAllDrivers]       = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [tab,              setTab]              = useState("pending");
  const [search,           setSearch]           = useState("");
  const [selected,         setSelected]         = useState(null);
  const [rejectReason,     setRejectReason]     = useState("");
  const [abstractSelected, setAbstractSelected] = useState(null);
  const [abstractRejectReason, setAbstractRejectReason] = useState("");
  const [processing,       setProcessing]       = useState(false);
  const [page,             setPage]             = useState(1);
  const [expandedNotes,    setExpandedNotes]    = useState(new Set());
  const [noteDrafts,       setNoteDrafts]       = useState({});
  const [savingNote,       setSavingNote]       = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "users"), where("role", "==", "driver")));
      setAllDrivers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [tab, search]);

  // ── Section expand/collapse ────────────────────────────────────────────────
  // Smart default: a section is collapsed when its document is in a "good"
  // (approved/verified) resting state, and expanded when it needs admin action
  // (pending / rejected / expired / not uploaded). A manual click always wins and
  // is remembered per driver+section via `expandedSections`.

  const docNeedsAction = (driver, section) => {
    switch (section) {
      case "photo": return driver.profilePicStatus !== "approved";
      case "cdl":   return driver.licenseStatus !== "verified";
      case "pow":   return driver.proofOfWorkStatus !== "verified";
      case "final": return !isFullyVerified(driver) || driver.licenseVerificationLocked;
      default:      return true;
    }
  };

  const toggleSection = (driverId, section) => {
    setExpandedSections(prev => {
      const cur = { ...(prev[driverId] || {}) };
      const currentlyOpen = cur[section] ?? null;
      // Flip relative to whatever is currently showing; store explicit boolean.
      cur[section] = !(currentlyOpen === null ? false : currentlyOpen);
      return { ...prev, [driverId]: cur };
    });
  };
  // If admin set an explicit value, use it; otherwise fall back to smart default.
  const isSectionOpen = (driver, section) => {
    const explicit = expandedSections[driver.id]?.[section];
    if (explicit !== undefined && explicit !== null) return explicit;
    return docNeedsAction(driver, section);
  };

  // ── Helpers ────────────────────────────────────────────────────────────────

  const isExpiringSoon = (d) => {
    const cdlDays = daysUntil(d.licenseExpiry);
    if (cdlDays !== null && cdlDays > 0 && cdlDays <= 30) return true;
    const powDays = daysUntil(d.proofOfWorkExpiry);
    if (powDays !== null && powDays > 0 && powDays <= 30) return true;
    if (d.abstractUploadedAt && d.abstractStatus !== "expired") {
      const daysOld = Math.floor((Date.now() - (d.abstractUploadedAt?.toMillis?.() || 0)) / 86400000);
      if (daysOld >= 20) return true;
    }
    return d.abstractStatus === "expiring_soon";
  };

  const getTabDrivers = (drivers, tabKey) => {
    switch (tabKey) {
      case "all_drivers": return drivers.filter(isFullyVerified);
      case "pending":     return drivers.filter(d =>
        d.profilePicStatus === "pending" || d.licenseStatus === "pending" ||
        d.abstractStatus   === "pending" || d.proofOfWorkStatus === "pending");
      case "rejected":    return drivers.filter(d =>
        d.profilePicStatus === "rejected" || d.licenseStatus === "rejected" ||
        d.abstractStatus   === "rejected" || d.proofOfWorkStatus === "rejected");
      case "expiring":    return drivers.filter(isExpiringSoon);
      default:            return drivers;
    }
  };

  const logAdminAction = async (driverId, action, detail) => {
    try {
      const admin = getAuth().currentUser;
      await addDoc(collection(db, "users", driverId, "adminActions"), {
        action, detail, adminEmail: admin?.email || "admin", timestamp: serverTimestamp(),
      });
    } catch (err) { console.warn("Failed to log admin action:", err); }
  };

  // ── Document-level handlers ────────────────────────────────────────────────

  const handleApproveCDL = async (driver) => {
    setProcessing(true);
    try {
      await updateDoc(doc(db, "users", driver.id), { licenseStatus: "verified", verifiedAt: serverTimestamp(), rejectionReason: null, licenseReopen: false, licenseExpiry30Notified: false, licenseExpiry7Notified: false });
      await logAdminAction(driver.id, "cdl_approved", "CDL approved by admin");
      load(); alert(`✅ CDL approved for ${driver.fullName}.`);
    } catch (err) { alert("Error updating status"); }
    finally { setProcessing(false); }
  };

  const handleRejectCDL = async (driver) => {
    const reason = window.prompt(`Rejection reason for ${driver.fullName}'s CDL:`);
    if (!reason?.trim()) return;
    setProcessing(true);
    try {
      // Clear the uploaded CDL images so the driver's Edit Profile can't pre-fill
      // the rejected photos — they must upload fresh ones.
      await updateDoc(doc(db, "users", driver.id), {
        licenseStatus: "rejected", rejectionReason: reason.trim(), rejectedAt: serverTimestamp(),
        licenseImageUrl: null, licenseImageBackUrl: null, licenseSelfieUrl: null,
        licenseFrontUploadedAt: null, licenseBackUploadedAt: null, licenseSelfieUploadedAt: null,
      });
      await logAdminAction(driver.id, "cdl_rejected", reason.trim());
      load(); alert(`❌ CDL rejected for ${driver.fullName}. They must re-upload all CDL photos.`);
    } catch (err) { alert("Error updating status"); }
    finally { setProcessing(false); }
  };

  const handleApprovePic = async (driver) => {
    if (!driver.profilePicUrl) { alert("Cannot approve — no profile photo on file."); return; }
    setProcessing(true);
    try {
      await updateDoc(doc(db, "users", driver.id), { profilePicStatus: "approved", profilePicApprovedAt: serverTimestamp(), profilePicReopen: false, profilePicRejectionReason: null, profilePicExpiry30Notified: false, profilePicExpiry7Notified: false });
      await logAdminAction(driver.id, "photo_approved", "Profile photo approved");
      load(); alert(`✅ Profile photo approved for ${driver.fullName}.`);
    } catch (err) { alert("Error approving photo."); }
    finally { setProcessing(false); }
  };

  const handleRejectPic = async (driver) => {
    const reason = window.prompt(`Rejection reason for ${driver.fullName}'s profile photo:\n(e.g. "Photo is blurry", "Not a real photo of a person", "Doesn't match CDL selfie")`);
    if (!reason?.trim()) return;
    setProcessing(true);
    try {
      await updateDoc(doc(db, "users", driver.id), { profilePicStatus: "rejected", profilePicRejectedAt: serverTimestamp(), profilePicUrl: null, profilePicRejectionReason: reason.trim() });
      await logAdminAction(driver.id, "photo_rejected", reason.trim());
      load(); alert(`❌ Profile photo rejected for ${driver.fullName}. They will be prompted to re-upload.`);
    } catch (err) { alert("Error rejecting photo."); }
    finally { setProcessing(false); }
  };

  const handleApproveAbstract = async (driver) => {
    if (!driver.abstractDocUrl) { alert("Cannot approve — no abstract on file."); return; }
    setProcessing(true);
    try {
      await updateDoc(doc(db, "users", driver.id), { abstractStatus: "verified", abstractVerifiedAt: serverTimestamp(), abstractRejectionReason: null, abstractReopen: false, abstractExpiry30Notified: false, abstractExpiry7Notified: false });
      await logAdminAction(driver.id, "abstract_approved", "Driver's abstract approved");
      load(); alert(`✅ Driver's abstract approved for ${driver.fullName}.`);
    } catch (err) { alert("Error approving abstract."); }
    finally { setProcessing(false); }
  };

  const handleRejectAbstract = async (driver) => {
    if (!abstractRejectReason.trim()) { alert("Please enter a rejection reason."); return; }
    setProcessing(true);
    try {
      await updateDoc(doc(db, "users", driver.id), { abstractStatus: "rejected", abstractRejectionReason: abstractRejectReason.trim(), abstractRejectedAt: serverTimestamp(), abstractDocUrl: null });
      await logAdminAction(driver.id, "abstract_rejected", abstractRejectReason.trim());
      setAbstractSelected(null); setAbstractRejectReason("");
      load(); alert(`❌ Abstract rejected for ${driver.fullName}. They will be prompted to re-upload.`);
    } catch (err) { alert("Error rejecting abstract."); }
    finally { setProcessing(false); }
  };

  const handleApprovePow = async (driver) => {
    if (!driver.proofOfWorkUrl) { alert("No document on file."); return; }
    setProcessing(true);
    try {
      await updateDoc(doc(db, "users", driver.id), { proofOfWorkStatus: "verified", proofOfWorkVerifiedAt: serverTimestamp(), proofOfWorkRejectionReason: null, proofOfWorkReopen: false, proofOfWorkExpiry30Notified: false, proofOfWorkExpiry7Notified: false });
      await logAdminAction(driver.id, "pow_approved", "Proof of Work approved");
      load(); alert(`✅ Proof of Work approved for ${driver.fullName}.`);
    } catch (err) { alert("Error approving."); }
    finally { setProcessing(false); }
  };

  const handleRejectPow = async (driver) => {
    const reason = window.prompt(`Rejection reason for ${driver.fullName}'s Proof of Work:\nAcceptable: Canadian Passport, PR Card, Work Permit, Birth Certificate`);
    if (!reason) return;
    setProcessing(true);
    try {
      await updateDoc(doc(db, "users", driver.id), { proofOfWorkStatus: "rejected", proofOfWorkRejectionReason: reason, proofOfWorkRejectedAt: serverTimestamp(), proofOfWorkUrl: null });
      await logAdminAction(driver.id, "pow_rejected", reason);
      load(); alert(`❌ Proof of Work rejected for ${driver.fullName}.`);
    } catch (err) { alert("Error rejecting."); }
    finally { setProcessing(false); }
  };

  // ── Reopen an approved document for re-upload ───────────────────────────────
  // Approved documents are read-only for the driver. This lets an admin reopen a
  // specific one (e.g. CrewBoard needs a fresher copy). It clears the stored file
  // and sets the doc back to "rejected" with a note, so it reuses the normal
  // re-upload → pending → review flow on the driver side. The matching reopen flag
  // tells the app to allow uploading again; it's cleared automatically on re-approval.
  const REOPEN_CONFIG = {
    license:     { label: "CDL",                statusField: "licenseStatus",     reopenField: "licenseReopen",     reasonField: "rejectionReason",            urlFields: ["licenseImageUrl", "licenseImageBackUrl", "licenseSelfieUrl"], action: "cdl_reopened" },
    proofOfWork: { label: "Proof of Work",      statusField: "proofOfWorkStatus", reopenField: "proofOfWorkReopen", reasonField: "proofOfWorkRejectionReason", urlFields: ["proofOfWorkUrl"], action: "pow_reopened" },
    abstract:    { label: "Driver's Abstract",  statusField: "abstractStatus",    reopenField: "abstractReopen",    reasonField: "abstractRejectionReason",    urlFields: ["abstractDocUrl"], action: "abstract_reopened" },
    profilePic:  { label: "Profile Photo",      statusField: "profilePicStatus",  reopenField: "profilePicReopen",  reasonField: "profilePicRejectionReason",  urlFields: ["profilePicUrl"], action: "photo_reopened" },
  };

  const handleReopenDoc = async (driver, key) => {
    const cfg = REOPEN_CONFIG[key];
    if (!cfg) return;
    const note = window.prompt(`Reopen ${cfg.label} for re-upload by ${driver.fullName}?\n\nOptionally add a note the driver will see (e.g. "We need a clearer copy"):`, "");
    if (note === null) return; // cancelled
    if (!window.confirm(`This will remove the current ${cfg.label} and ask ${driver.fullName} to upload a new one. Continue?`)) return;
    setProcessing(true);
    try {
      const updates = {
        [cfg.statusField]: "rejected",
        [cfg.reopenField]: true,
        [cfg.reasonField]: note.trim() || `${cfg.label} reopened by admin — please upload a current copy.`,
      };
      cfg.urlFields.forEach((f) => { updates[f] = null; });
      await updateDoc(doc(db, "users", driver.id), updates);
      await logAdminAction(driver.id, cfg.action, note.trim() || "Reopened for re-upload");
      load(); alert(`🔓 ${cfg.label} reopened for ${driver.fullName}. They can now upload a new one.`);
    } catch (err) { alert("Error reopening document."); }
    finally { setProcessing(false); }
  };

  // ── Final Decision handlers ────────────────────────────────────────────────

  const handleApproveProfile = async (driver) => {
    const missing = [];
    // All four documents are mandatory for full verification.
    if (!driver.profilePicUrl)                          missing.push("📸 Profile Photo not uploaded");
    else if (driver.profilePicStatus !== "approved")    missing.push("📸 Profile Photo is not approved");
    if (driver.licenseStatus !== "verified")            missing.push("🪪 CDL is not verified");
    if (!driver.proofOfWorkUrl)                          missing.push("🛂 Proof of Work not uploaded");
    else if (driver.proofOfWorkStatus !== "verified")   missing.push("🛂 Proof of Work is not verified");
    if (!driver.abstractDocUrl)                          missing.push("📄 Driver's Abstract not uploaded");
    else if (driver.abstractStatus !== "verified")      missing.push("📄 Driver's Abstract is not verified");

    if (missing.length > 0) {
      alert(`Cannot mark this driver fully verified — the following are still outstanding:\n\n${missing.map(m => `• ${m}`).join("\n")}\n\nApprove each document section first.`);
      return;
    }

    if (!window.confirm(`Approve ${driver.fullName}'s full profile? All four documents are verified. This marks the driver as fully verified — they will get the verified badge and can apply to jobs.`)) return;
    setProcessing(true);
    try {
      await updateDoc(doc(db, "users", driver.id), { licenseStatus: "verified", verifiedAt: serverTimestamp(), rejectionReason: null, licenseReopen: false, licenseExpiry30Notified: false, licenseExpiry7Notified: false });
      await logAdminAction(driver.id, "profile_approved", "Full profile approved by admin");
      load(); alert(`✅ ${driver.fullName}'s profile approved! Approval email sent automatically by Cloud Function.`);
    } catch (err) { alert("Error updating status"); }
    finally { setProcessing(false); }
  };

  const handleRejectProfileConfirm = async (driver) => {
    if (!rejectReason.trim()) { alert("Please enter a rejection reason."); return; }
    setProcessing(true);
    try {
      const currentAttempts = driver.licenseVerificationAttempts || 0;
      const newAttempts     = currentAttempts + 1;
      const isNowLocked     = newAttempts >= 3;
      await updateDoc(doc(db, "users", driver.id), {
        licenseStatus: "rejected", rejectionReason: rejectReason, rejectedAt: serverTimestamp(),
        licenseVerificationAttempts: newAttempts,
        // Clear CDL images so Edit Profile can't pre-fill rejected photos.
        licenseImageUrl: null, licenseImageBackUrl: null, licenseSelfieUrl: null,
        licenseFrontUploadedAt: null, licenseBackUploadedAt: null, licenseSelfieUploadedAt: null,
        ...(isNowLocked && { licenseVerificationLocked: true }),
      });
      await logAdminAction(driver.id, "profile_rejected", `Attempt ${newAttempts}/3 — ${rejectReason}${isNowLocked ? " — LOCKED" : ""}`);
      setSelected(null); setRejectReason("");
      load();
      alert(isNowLocked
        ? `❌ Profile rejected. ${driver.fullName} has used all 3 attempts and is now locked — they must contact support@crewboard.ca.`
        : `❌ Profile rejected. Rejection email sent to ${driver.email}. Attempt ${newAttempts}/3.`
      );
    } catch (err) { alert("Error updating status"); }
    finally { setProcessing(false); }
  };

  const handleSuspend = async (driver) => {
    if (!window.confirm(`Suspend ${driver.fullName}'s account?`)) return;
    await updateDoc(doc(db, "users", driver.id), { accountStatus: "suspended", suspendedAt: serverTimestamp(), suspendedReason: "Manually suspended by admin" });
    await logAdminAction(driver.id, "suspended", "Account suspended by admin");
    load();
  };

  const handleReactivate = async (driver) => {
    await updateDoc(doc(db, "users", driver.id), { accountStatus: "active", suspendedReason: null });
    await logAdminAction(driver.id, "reactivated", "Account reactivated by admin");
    load();
  };

  const handleUnlockVerification = async (driver) => {
    const confirmed = window.confirm(
      `Unlock CDL verification for ${driver.fullName}?\n\n` +
      `• Resets attempt counter to 0 (fresh 3 attempts)\n` +
      `• Clears the lock\n` +
      `• Resets license status to "not uploaded" — they must re-upload fresh photos\n\n` +
      `Only do this after reviewing their support email.`
    );
    if (!confirmed) return;
    setProcessing(true);
    try {
      await updateDoc(doc(db, "users", driver.id), {
        licenseVerificationAttempts: 0, licenseVerificationLocked: false,
        licenseStatus: "not_uploaded",  rejectionReason: null,
        verificationUnlockedAt: serverTimestamp(), verificationUnlockedBy: "admin",
      });
      await logAdminAction(driver.id, "verification_unlocked", "Attempt counter reset, status set to not_uploaded");
      load(); alert(`✅ Verification unlocked for ${driver.fullName}. Attempt counter reset to 0.`);
    } catch (err) { alert("Error unlocking verification."); }
    finally { setProcessing(false); }
  };

  const handleClearNoShow = async (driver) => {
    if (!window.confirm(`Clear ${driver.noShowCount || 0} no-show report(s) for ${driver.fullName}?`)) return;
    setProcessing(true);
    try {
      await updateDoc(doc(db, "users", driver.id), { noShowCount: 0, noShowFlagged: false, noShowClearedAt: serverTimestamp(), noShowClearedBy: "admin" });
      await logAdminAction(driver.id, "noshows_cleared", `Cleared ${driver.noShowCount} no-show(s)`);
      load(); alert(`✅ No-show reports cleared for ${driver.fullName}.`);
    } catch (err) { alert("Error clearing no-show flag."); }
    finally { setProcessing(false); }
  };

  const handleDelete = async (driver) => {
    const typed = window.prompt(`⚠️ PERMANENT DELETION\n\nType "${driver.fullName}" to confirm deleting this account.\n\nCloud Function will purge ALL data.`);
    if (typed !== driver.fullName) { if (typed !== null) alert("Name did not match. Deletion cancelled."); return; }
    try {
      await addDoc(collection(db, "dataRequests"), {
        userId: driver.id, userEmail: driver.email, type: "deletion", status: "completed",
        requestedBy: "admin", requestedAt: serverTimestamp(), completedAt: serverTimestamp(), completedBy: "admin",
      });
      await updateDoc(doc(db, "users", driver.id), { accountStatus: "pending_deletion", deletionRequestedAt: serverTimestamp() });
      await logAdminAction(driver.id, "deletion_initiated", "Account deletion initiated by admin");
      load(); alert(`🗑️ Deletion initiated for ${driver.fullName}. Cloud Function is purging all data.`);
    } catch (err) { alert("Error deleting user."); }
  };

  // ── Notes ──────────────────────────────────────────────────────────────────

  const toggleNotes = (driverId) => {
    setExpandedNotes(prev => {
      const next = new Set(prev);
      if (next.has(driverId)) { next.delete(driverId); }
      else {
        next.add(driverId);
        const currentNote = allDrivers.find(d => d.id === driverId)?.adminNote || "";
        setNoteDrafts(p => ({ ...p, [driverId]: currentNote }));
      }
      return next;
    });
  };

  const handleSaveNote = async (driver) => {
    const note = noteDrafts[driver.id] ?? "";
    setSavingNote(driver.id);
    try {
      await updateDoc(doc(db, "users", driver.id), { adminNote: note, adminNoteUpdatedAt: serverTimestamp() });
      setAllDrivers(prev => prev.map(d => d.id === driver.id ? { ...d, adminNote: note } : d));
    } catch (err) { alert("Error saving note."); }
    finally { setSavingNote(null); }
  };

  // ── Derived state ──────────────────────────────────────────────────────────

  const tabDrivers = getTabDrivers(allDrivers, tab);
  const s          = search.trim().toLowerCase();
  const displayed  = s ? tabDrivers.filter(d =>
    d.fullName?.toLowerCase().includes(s) || d.email?.toLowerCase().includes(s) ||
    d.phone?.includes(s)                  || d.location?.toLowerCase().includes(s) ||
    d.licenseClass?.toLowerCase().includes(s) || d.licenseNumber?.toLowerCase().includes(s)
  ) : tabDrivers;
  const totalPages = Math.ceil(displayed.length / PAGE_SIZE);
  const paginated  = displayed.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const tabCounts  = TABS.reduce((acc, t) => { acc[t.key] = getTabDrivers(allDrivers, t.key).length; return acc; }, {});
  const stats      = { verified: allDrivers.filter(isFullyVerified).length, pending: tabCounts.pending, rejected: tabCounts.rejected, expiring: tabCounts.expiring };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="animate-fade">

      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: "Syne", fontSize: 28, fontWeight: 800, color: "var(--text)" }}>Driver Verification</h1>
        <p style={{ color: "var(--text-muted)", marginTop: 4 }}>Review and verify driver document submissions</p>
      </div>

      <div style={{ background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 12, padding: "12px 18px", marginBottom: 20, display: "flex", gap: 12, alignItems: "center" }}>
        <span style={{ fontSize: 16 }}>☁️</span>
        <div style={{ color: "var(--text-secondary)", fontSize: 13, lineHeight: 1.5 }}>
          <strong style={{ color: "#22C55E" }}>Cloud Functions Active</strong> — Approve, reject, and suspend actions automatically trigger driver emails. No manual emails needed.
        </div>
      </div>

      {/* Stats bar */}
      {!loading && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 20 }}>
          {[
            { label: "Verified",        value: stats.verified, color: "#22C55E" },
            { label: "Pending Review",  value: stats.pending,  color: "#EAB308" },
            { label: "Rejected",        value: stats.rejected, color: "#EF4444" },
            { label: "About to Expire", value: stats.expiring, color: "#F97316" },
          ].map(s => (
            <div key={s.label} style={{ background: "var(--surface)", border: "1px solid var(--surface2)", borderRadius: 10, padding: "12px 16px" }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color, fontFamily: "Syne" }}>{s.value}</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Search by name, email, location, license class or number..."
          style={{ flex: 1, background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 16px", color: "var(--text)", fontSize: 13, outline: "none" }}
        />
        {search && <button onClick={() => setSearch("")} style={{ background: "transparent", border: "none", color: "var(--text-muted)", fontSize: 18, cursor: "pointer", padding: "0 4px" }}>✕</button>}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {TABS.map(t => {
          const count    = tabCounts[t.key];
          const isActive = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              background: isActive ? "#F97316" : "var(--surface)",
              border: `1px solid ${isActive ? "#F97316" : "var(--border)"}`,
              color: isActive ? "#fff" : "var(--text-secondary)",
              borderRadius: 8, padding: "9px 20px", fontSize: 13, fontWeight: 600,
              cursor: "pointer", transition: "all 0.15s",
            }}>
              {t.label}
              {count > 0 && (
                <span style={{ marginLeft: 6, background: isActive ? "rgba(255,255,255,0.25)" : "rgba(249,115,22,0.15)", color: isActive ? "#fff" : "#F97316", borderRadius: 20, padding: "1px 7px", fontSize: 11 }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div style={{ color: "var(--text-muted)", textAlign: "center", padding: 60 }}>Loading drivers...</div>
      ) : (
        <div>
          <div style={{ color: "var(--text-muted)", fontSize: 12, marginBottom: 16 }}>
            {search ? `${displayed.length} result${displayed.length !== 1 ? "s" : ""} for "${search}"` : `${displayed.length} driver${displayed.length !== 1 ? "s" : ""}`}
          </div>

          {displayed.length === 0 ? (
            <div style={{ color: "var(--text-muted)", textAlign: "center", padding: 60, background: "var(--surface)", borderRadius: 16, border: "1px solid var(--surface2)" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
              <div>{search ? `No drivers match "${search}"` : "No drivers in this category"}</div>
              {search && <button onClick={() => setSearch("")} style={{ marginTop: 12, background: "transparent", border: "1px solid var(--text-muted)", color: "var(--text-secondary)", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 13 }}>Clear search</button>}
            </div>
          ) : (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {paginated.map(driver => {
                  const isLocked    = driver.licenseVerificationLocked;
                  const isSuspended = driver.accountStatus === "suspended";
                  const isDeletion  = driver.accountStatus === "pending_deletion";
                  const notesOpen   = expandedNotes.has(driver.id);

                  const photoReUploaded    = driver.profilePicStatus    === "pending" && driver.profilePicRejectedAt;
                  const abstractReUploaded = driver.abstractStatus      === "pending" && driver.abstractRejectedAt;
                  const powReUploaded      = driver.proofOfWorkStatus   === "pending" && driver.proofOfWorkRejectedAt;

                  const reUploadBadge = (
                    <span style={{ background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.3)", color: "#3B82F6", borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 600 }}>
                      🔄 Re-uploaded — needs review
                    </span>
                  );

                  const fullyVerified = isFullyVerified(driver);
                  const overall = isSuspended
                    ? { label: "Suspended", color: "#EF4444", bg: "rgba(239,68,68,0.1)", bd: "rgba(239,68,68,0.3)" }
                    : isLocked
                    ? { label: "Locked — 3/3 attempts", color: "#EF4444", bg: "rgba(239,68,68,0.1)", bd: "rgba(239,68,68,0.3)" }
                    : fullyVerified
                    ? { label: "Fully Verified", color: "#22C55E", bg: "rgba(34,197,94,0.1)", bd: "rgba(34,197,94,0.3)" }
                    : { label: "Verification incomplete", color: "#EAB308", bg: "rgba(234,179,8,0.1)", bd: "rgba(234,179,8,0.3)" };

                  return (
                    <div key={driver.id} style={{ background: "var(--surface)", border: "1px solid var(--surface2)", borderRadius: 16, overflow: "hidden", borderLeft: `3px solid ${overall.color}` }}>

                      {/* ── Driver Header ──────────────────────────────────── */}
                      <div style={{ padding: "18px 20px" }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
                          <div style={{ width: 52, height: 52, borderRadius: 12, background: "#F97316", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Syne", fontWeight: 800, fontSize: 21, color: "#fff", flexShrink: 0, overflow: "hidden" }}>
                            {driver.profilePicUrl
                              ? <img src={driver.profilePicUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              : driver.fullName?.charAt(0)}
                          </div>
                          <div style={{ flex: 1, minWidth: 220 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 5 }}>
                              <span style={{ color: "var(--text)", fontWeight: 700, fontSize: 17, letterSpacing: "-0.01em" }}>{driver.fullName}</span>
                              <span style={{ background: overall.bg, border: `1px solid ${overall.bd}`, color: overall.color, borderRadius: 20, padding: "2px 11px", fontSize: 11.5, fontWeight: 600 }}>{overall.label}</span>
                              {isDeletion && <span style={{ background: "rgba(234,179,8,0.1)", border: "1px solid rgba(234,179,8,0.3)", color: "#EAB308", borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 600 }}>⏳ Pending Deletion</span>}
                              {(driver.planId === "pro_monthly" || driver.planId === "pro_yearly") && <span style={{ background: "rgba(249,115,22,0.15)", border: "1px solid rgba(249,115,22,0.3)", color: "#F97316", borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 600 }}>⭐ PRO</span>}
                            </div>
                            <div style={{ display: "flex", gap: "6px 18px", flexWrap: "wrap", color: "var(--text-secondary)", fontSize: 12.5, marginBottom: 6 }}>
                              <span>✉️ {driver.email}</span>
                              <span>📱 {driver.phone || "N/A"}</span>
                              <span>📍 {driver.location || "N/A"}</span>
                              <span>🪪 Class {driver.licenseClass || "N/A"} · #{driver.licenseNumber || "N/A"}</span>
                              <span>📅 Expires {formatDate(driver.licenseExpiry, "N/A")}</span>
                              <span>💰 ${driver.hourlyRate || "—"}/hr</span>
                            </div>
                            {driver.noShowCount > 0 && (
                              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                <span style={{ color: driver.noShowFlagged ? "#EF4444" : "#EAB308", fontWeight: 600, fontSize: 12 }}>
                                  {driver.noShowFlagged ? "🚩" : "⚠️"} {driver.noShowCount} no-show{driver.noShowCount !== 1 ? "s" : ""}
                                </span>
                                <button onClick={() => handleClearNoShow(driver)} disabled={processing} style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--text-muted)", borderRadius: 6, padding: "2px 8px", fontSize: 11, cursor: "pointer" }}>Clear</button>
                              </div>
                            )}
                            {driver.adminNote && <div style={{ color: "#A855F7", fontSize: 12, marginBottom: 2 }}>📝 <em>{driver.adminNote}</em></div>}
                          </div>
                          <button onClick={() => toggleNotes(driver.id)} style={{ background: notesOpen ? "rgba(168,85,247,0.1)" : "transparent", border: `1px solid ${notesOpen ? "rgba(168,85,247,0.35)" : "var(--border)"}`, color: notesOpen ? "#A855F7" : "var(--text-muted)", borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: "pointer", flexShrink: 0 }}>
                            📝 Notes
                          </button>
                        </div>

                        {/* At-a-glance document status row */}
                        <div style={{ display: "flex", gap: "8px 18px", flexWrap: "wrap", marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--surface2)" }}>
                          <MiniStat icon="📸 " label="Photo"    status={driver.profilePicStatus} />
                          <MiniStat icon="🪪 " label="CDL"      status={driver.licenseStatus} />
                          <MiniStat icon="🛂 " label="Proof of Work" status={driver.proofOfWorkStatus} />
                          <MiniStat icon="📄 " label="Abstract" status={driver.abstractStatus} />
                        </div>
                      </div>

                      {/* ── Section 1: Profile Photo ───────────────────────── */}
                      <Section
                        title="📸 Profile Photo"
                        badge={<Badge status={driver.profilePicStatus} />}
                        alertBadge={photoReUploaded ? reUploadBadge : null}
                        accent={metaFor(driver.profilePicStatus).accent}
                        isOpen={isSectionOpen(driver, "photo")}
                        onToggle={() => toggleSection(driver.id, "photo")}
                      >
                        <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 16, alignItems: "start" }}>
                          <PhotoCol url={driver.profilePicUrl} emptyText="No photo uploaded" borderColor="#3B82F6" />
                          <div>
                            {driver.profilePicStatus === "rejected" && (
                              <div style={{ background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "#EF4444", marginBottom: 12 }}>
                                Rejected{driver.profilePicRejectionReason ? `: ${driver.profilePicRejectionReason}` : ""} — driver will be prompted to re-upload
                              </div>
                            )}
                            {!driver.profilePicUrl ? (
                              <span style={{ color: "var(--text-muted)", fontSize: 12 }}>No photo uploaded yet</span>
                            ) : (
                              <>
                              {driver.profilePicStatus === "approved" && driver.profilePicReopen === true && <AutoReopenBadge />}
                              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                {driver.profilePicStatus !== "approved" && (
                                  <button onClick={() => handleApprovePic(driver)} disabled={processing} style={btn("approve")}>✅ Approve Photo</button>
                                )}
                                {driver.profilePicStatus !== "rejected" && (
                                  <button onClick={() => handleRejectPic(driver)} disabled={processing} style={btn("reject")}>❌ Reject Photo</button>
                                )}
                                {driver.profilePicStatus === "approved" && (
                                  <button onClick={() => handleReopenDoc(driver, "profilePic")} disabled={processing} style={btn("neutral")} title={driver.profilePicReopen ? "Already open for renewal. This forces a rejected state and removes the verified badge." : "Remove the approved photo and ask the driver to upload a new one"}>{driver.profilePicReopen ? "🔒 Force re-upload (drops verified)" : "🔓 Reopen for re-upload"}</button>
                                )}
                              </div>
                              </>
                            )}
                          </div>
                        </div>
                      </Section>

                      {/* ── Section 2: CDL ──────────────────────────────────── */}
                      <Section
                        title="🪪 CDL"
                        badge={<Badge status={driver.licenseStatus} />}
                        alertBadge={
                          isLocked ? <span style={{ color: "#EF4444", fontWeight: 700, fontSize: 12 }}>🔒 Max attempts reached</span>
                          : driver.licenseVerificationAttempts > 0 ? <span style={{ color: "#EAB308", fontWeight: 600, fontSize: 12 }}>⚠️ Attempt {driver.licenseVerificationAttempts}/3</span>
                          : null
                        }
                        accent={metaFor(driver.licenseStatus).accent}
                        isOpen={isSectionOpen(driver, "cdl")}
                        onToggle={() => toggleSection(driver.id, "cdl")}
                      >
                        {/* Freshness after unlock */}
                        {driver.verificationUnlockedAt && (() => {
                          const unlockedMs = driver.verificationUnlockedAt?.toMillis?.() || 0;
                          const frontNew   = (driver.licenseFrontUploadedAt?.toMillis?.()  || 0) > unlockedMs;
                          const backNew    = (driver.licenseBackUploadedAt?.toMillis?.()   || 0) > unlockedMs;
                          const selfieNew  = (driver.licenseSelfieUploadedAt?.toMillis?.() || 0) > unlockedMs;
                          const allNew     = frontNew && backNew && selfieNew;
                          const noneNew    = !frontNew && !backNew && !selfieNew;
                          const unlockedDate = formatDate(driver.verificationUnlockedAt);
                          return (
                            <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                              {allNew   && <span style={{ background: "rgba(34,197,94,0.1)",   border: "1px solid rgba(34,197,94,0.3)",   color: "#22C55E", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>✅ All photos new since unlock</span>}
                              {noneNew  && <span style={{ background: "rgba(239,68,68,0.08)",  border: "1px solid rgba(239,68,68,0.25)",  color: "#EF4444", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>⚠️ No photos updated since unlock ({unlockedDate})</span>}
                              {!allNew && !noneNew && <span style={{ background: "rgba(234,179,8,0.08)", border: "1px solid rgba(234,179,8,0.25)", color: "#EAB308", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>⚠️ Partially updated since unlock ({unlockedDate})</span>}
                            </div>
                          );
                        })()}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12, marginBottom: 14 }}>
                          <PhotoCol label="📄 Front"           url={driver.licenseImageUrl}     emptyText="Front not uploaded" />
                          <PhotoCol label="🔄 Back"            url={driver.licenseImageBackUrl} emptyText="Back not uploaded" />
                          <PhotoCol label="🤳 Selfie with CDL" url={driver.licenseSelfieUrl}    emptyText="Selfie not uploaded" borderColor="#F97316" subText="Identity — not shown to hirers" />
                        </div>
                        {driver.rejectionReason && (
                          <div style={{ background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "#EF4444", marginBottom: 12 }}>
                            Last rejection reason: {driver.rejectionReason}
                          </div>
                        )}
                        {driver.licenseStatus === "verified" && driver.licenseReopen === true && <AutoReopenBadge />}
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          {driver.licenseImageUrl && driver.licenseStatus !== "verified" && <button onClick={() => handleApproveCDL(driver)} disabled={processing} style={btn("approve")}>✅ Approve CDL</button>}
                          {driver.licenseStatus !== "rejected" && <button onClick={() => handleRejectCDL(driver)}  disabled={processing} style={btn("reject")}>❌ Reject CDL</button>}
                          {driver.licenseStatus === "verified" && <button onClick={() => handleReopenDoc(driver, "license")} disabled={processing} style={btn("neutral")} title={driver.licenseReopen ? "Already open for renewal. This forces a rejected state and removes the verified badge." : "Remove the approved CDL and ask the driver to upload a new one"}>{driver.licenseReopen ? "🔒 Force re-upload (drops verified)" : "🔓 Reopen for re-upload"}</button>}
                          {isLocked && <button onClick={() => handleUnlockVerification(driver)} disabled={processing} style={btn("purple")}>🔓 Unlock Verification</button>}
                        </div>

                        {/* Abstract sub-section — render when a file exists OR a status is set
                            (so a rejected/cleared abstract still shows its state). */}
                        {(driver.abstractDocUrl || driver.abstractStatus) && (
                          <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px dashed var(--surface2)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)" }}>📄 Driver's Abstract</span>
                              <Badge status={driver.abstractStatus} />
                              {abstractReUploaded && reUploadBadge}
                              {driver.abstractDocUrl && driver.abstractUploadedAt && (() => {
                                const days = Math.floor((Date.now() - (driver.abstractUploadedAt?.toMillis?.() || 0)) / 86400000);
                                return days > 30
                                  ? <span style={{ color: "#EF4444", fontSize: 11, fontWeight: 600 }}>⚠️ {days} days old — exceeds 30-day policy</span>
                                  : <span style={{ color: "#22C55E", fontSize: 11 }}>✅ {days} day{days !== 1 ? "s" : ""} old</span>;
                              })()}
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 12, alignItems: "start", marginBottom: 10 }}>
                              <PhotoCol url={driver.abstractDocUrl} emptyText={driver.abstractStatus === "rejected" ? "Rejected — awaiting re-upload" : "No abstract uploaded"} borderColor="#3B82F6" />
                              {driver.abstractRejectionReason && <div style={{ color: "#EF4444", fontSize: 12 }}>Rejected: {driver.abstractRejectionReason}</div>}
                            </div>
                            {driver.abstractStatus === "verified" && driver.abstractReopen === true && <AutoReopenBadge />}
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              {driver.abstractDocUrl && driver.abstractStatus !== "verified" && <button onClick={() => handleApproveAbstract(driver)} disabled={processing} style={btn("approve", { fontSize: 12, padding: "7px 14px" })}>✅ Approve Abstract</button>}
                              {driver.abstractDocUrl && driver.abstractStatus !== "rejected" && <button onClick={() => setAbstractSelected(driver)} style={btn("reject", { fontSize: 12, padding: "7px 14px" })}>❌ Reject Abstract</button>}
                              {driver.abstractStatus === "verified" && <button onClick={() => handleReopenDoc(driver, "abstract")} disabled={processing} style={btn("neutral", { fontSize: 12, padding: "7px 14px" })} title={driver.abstractReopen ? "Already open for renewal. This forces a rejected state and removes the verified badge." : "Remove the approved abstract and ask the driver to upload a new one"}>{driver.abstractReopen ? "🔒 Force re-upload" : "🔓 Reopen"}</button>}
                            </div>
                          </div>
                        )}
                      </Section>

                      {/* ── Section 3: Proof of Work ──────────────────────── */}
                      <Section
                        title="🛂 Proof of Work"
                        badge={<Badge status={driver.proofOfWorkStatus} />}
                        alertBadge={powReUploaded ? reUploadBadge : null}
                        accent={metaFor(driver.proofOfWorkStatus).accent}
                        isOpen={isSectionOpen(driver, "pow")}
                        onToggle={() => toggleSection(driver.id, "pow")}
                      >
                        <div style={{ background: "rgba(59,130,246,0.07)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12, color: "var(--text-secondary)" }}>
                          <strong style={{ color: "var(--text)" }}>✅ Acceptable:</strong> Canadian Passport · PR Card · Valid Work Permit · Canadian Birth Certificate
                        </div>
                        {driver.proofOfWorkUrl ? (
                          <>
                            <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 16, alignItems: "start", marginBottom: 12 }}>
                              <PhotoCol url={driver.proofOfWorkUrl} emptyText="No document uploaded" borderColor="#A855F7" />
                              <div>
                                <div style={{ color: "var(--text-secondary)", fontSize: 12, marginBottom: 6 }}>
                                  {POW_TYPE_LABELS[driver.proofOfWorkType] || driver.proofOfWorkType || "Type not specified"}
                                  {driver.proofOfWorkExpiry && ` · Expires: ${formatDate(driver.proofOfWorkExpiry)}`}
                                </div>
                                {driver.proofOfWorkRejectionReason && <div style={{ color: "#EF4444", fontSize: 12 }}>Rejected: {driver.proofOfWorkRejectionReason}</div>}
                              </div>
                            </div>
                            {driver.proofOfWorkStatus === "verified" && driver.proofOfWorkReopen === true && <AutoReopenBadge />}
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              {driver.proofOfWorkUrl && driver.proofOfWorkStatus !== "verified" && <button onClick={() => handleApprovePow(driver)} disabled={processing} style={btn("approve")}>✅ Approve POW</button>}
                              {driver.proofOfWorkStatus !== "rejected" && <button onClick={() => handleRejectPow(driver)}  disabled={processing} style={btn("reject")}>❌ Reject POW</button>}
                              {driver.proofOfWorkStatus === "verified" && <button onClick={() => handleReopenDoc(driver, "proofOfWork")} disabled={processing} style={btn("neutral")} title={driver.proofOfWorkReopen ? "Already open for renewal. This forces a rejected state and removes the verified badge." : "Remove the approved Proof of Work and ask the driver to upload a new one"}>{driver.proofOfWorkReopen ? "🔒 Force re-upload (drops verified)" : "🔓 Reopen for re-upload"}</button>}
                            </div>
                          </>
                        ) : driver.proofOfWorkStatus === "rejected" ? (
                          <div style={{ background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "#EF4444" }}>
                            Rejected{driver.proofOfWorkRejectionReason ? `: ${driver.proofOfWorkRejectionReason}` : ""} — driver will be prompted to re-upload.
                          </div>
                        ) : (
                          <span style={{ color: "var(--text-muted)", fontSize: 12 }}>No document uploaded yet</span>
                        )}
                      </Section>

                      {/* ── Section 4: Final Decision ──────────────────────── */}
                      {isDeletion ? (
                        <div style={{ padding: "16px 20px", background: "rgba(234,179,8,0.04)" }}>
                          <span style={{ color: "#EAB308", fontSize: 13, fontWeight: 600 }}>⏳ Awaiting deletion — all data will be purged within 30 days by Cloud Function</span>
                        </div>
                      ) : (
                        <Section
                          title="⚖️ Final Decision"
                          badge={null}
                          alertBadge={
                            isLocked
                              ? <span style={{ color: "#EF4444", fontSize: 12, fontWeight: 700 }}>🔒 Locked — 3/3 attempts used</span>
                              : (driver.licenseVerificationAttempts || 0) > 0
                              ? <span style={{ color: "#EAB308", fontSize: 12, fontWeight: 600 }}>⚠️ {driver.licenseVerificationAttempts}/3 attempts used</span>
                              : null
                          }
                          accent={overall.color}
                          isOpen={isSectionOpen(driver, "final")}
                          onToggle={() => toggleSection(driver.id, "final")}
                        >
                          <div style={{ color: "var(--text-muted)", fontSize: 12, marginBottom: 12 }}>
                            These actions affect the driver's overall account. "Reject Profile" uses the 3-attempt counter.
                          </div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {driver.licenseStatus !== "verified" && (
                              <button onClick={() => handleApproveProfile(driver)} disabled={processing} style={btn("approve", { fontSize: 13, padding: "9px 20px" })}>✅ Approve Profile</button>
                            )}
                            {!isLocked ? (
                              <button onClick={() => setSelected(driver)} disabled={processing} style={btn("reject", { fontSize: 13, padding: "9px 20px" })}>
                                ❌ Reject Profile{(driver.licenseVerificationAttempts || 0) > 0 ? ` (${driver.licenseVerificationAttempts}/3)` : ""}
                              </button>
                            ) : (
                              <button onClick={() => handleUnlockVerification(driver)} disabled={processing} style={btn("purple", { fontSize: 13, padding: "9px 20px" })}>🔓 Unlock (Reset Attempts)</button>
                            )}
                            {!isSuspended
                              ? <button onClick={() => handleSuspend(driver)}    style={btn("neutral", { fontSize: 13, padding: "9px 20px" })}>⛔ Suspend</button>
                              : <button onClick={() => handleReactivate(driver)} style={btn("approve", { fontSize: 13, padding: "9px 20px" })}>🔓 Reactivate</button>
                            }
                            <button onClick={() => handleDelete(driver)} style={btn("reject", { fontSize: 13, padding: "9px 20px", background: "transparent" })}>🗑️ Delete</button>
                          </div>
                        </Section>
                      )}

                      {/* ── Admin Notes (expandable) ───────────────────────── */}
                      {notesOpen && (
                        <div style={{ padding: "14px 20px", background: "rgba(168,85,247,0.04)", borderTop: "1px solid var(--surface2)" }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#A855F7", marginBottom: 8 }}>📝 Admin Notes (internal only — not visible to driver)</div>
                          <textarea
                            value={noteDrafts[driver.id] ?? ""}
                            onChange={e => setNoteDrafts(p => ({ ...p, [driver.id]: e.target.value }))}
                            placeholder="Add an internal note about this driver..."
                            style={{ width: "100%", background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 8, padding: 12, color: "var(--text)", fontSize: 13, resize: "vertical", minHeight: 80, outline: "none", fontFamily: "DM Sans", boxSizing: "border-box" }}
                          />
                          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                            <button onClick={() => handleSaveNote(driver)} disabled={savingNote === driver.id} style={btn("purple", { fontSize: 12, padding: "7px 16px" })}>
                              {savingNote === driver.id ? "Saving..." : "Save Note"}
                            </button>
                            <button onClick={() => toggleNotes(driver.id)} style={btn("neutral", { fontSize: 12, padding: "7px 14px" })}>Cancel</button>
                          </div>
                        </div>
                      )}

                    </div>
                  );
                })}
              </div>
              {displayed.length > PAGE_SIZE && (
                <Paginator page={page} totalPages={totalPages} totalItems={displayed.length} pageSize={PAGE_SIZE} onChange={setPage} />
              )}
            </>
          )}
        </div>
      )}

      {/* ── Reject Profile Modal ───────────────────────────────────────────── */}
      {selected && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 24 }}>
          <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 32, width: "100%", maxWidth: 440 }}>
            <h3 style={{ fontFamily: "Syne", color: "var(--text)", marginBottom: 8 }}>Reject Profile</h3>
            <p style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 8 }}>
              Rejecting <strong style={{ color: "var(--text)" }}>{selected.fullName}</strong>'s driver profile.
            </p>
            {(selected.licenseVerificationAttempts || 0) >= 2 && (
              <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: "#EF4444", fontWeight: 600 }}>
                ⚠️ This is attempt {(selected.licenseVerificationAttempts || 0) + 1}/3 — account will be LOCKED after this rejection
              </div>
            )}
            <div style={{ background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#22C55E" }}>
              ☁️ Rejection email with your reason will be sent automatically to {selected.email}
            </div>
            <textarea
              value={rejectReason} onChange={e => setRejectReason(e.target.value)}
              placeholder="e.g. License photo is blurry. Please re-upload a clear photo of the front of your CDL."
              style={{ width: "100%", background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 10, padding: 14, color: "var(--text)", fontSize: 13, resize: "vertical", minHeight: 100, outline: "none", fontFamily: "DM Sans", boxSizing: "border-box" }}
            />
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={() => { setSelected(null); setRejectReason(""); }} style={{ flex: 1, background: "var(--surface2)", border: "1px solid var(--border)", color: "var(--text-secondary)", borderRadius: 8, padding: 10, cursor: "pointer" }}>Cancel</button>
              <button onClick={() => handleRejectProfileConfirm(selected)} disabled={processing} style={{ flex: 1, background: "#EF4444", border: "none", color: "#fff", borderRadius: 8, padding: 10, cursor: "pointer", fontWeight: 700 }}>
                {processing ? "Rejecting..." : "Reject Profile & Send Email"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Abstract Reject Modal ──────────────────────────────────────────── */}
      {abstractSelected && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 24 }}>
          <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 32, width: "100%", maxWidth: 440 }}>
            <h3 style={{ fontFamily: "Syne", color: "var(--text)", marginBottom: 8 }}>Reject Driver's Abstract</h3>
            <p style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 16 }}>
              Rejecting abstract for <strong style={{ color: "var(--text)" }}>{abstractSelected.fullName}</strong>. They must upload a new one.
            </p>
            <textarea
              value={abstractRejectReason} onChange={e => setAbstractRejectReason(e.target.value)}
              placeholder="e.g. Abstract is more than 30 days old. Please upload a fresh one from a registry office."
              style={{ width: "100%", background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 10, padding: 14, color: "var(--text)", fontSize: 13, resize: "vertical", minHeight: 100, outline: "none", fontFamily: "DM Sans", boxSizing: "border-box" }}
            />
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={() => { setAbstractSelected(null); setAbstractRejectReason(""); }} style={{ flex: 1, background: "var(--surface2)", border: "1px solid var(--border)", color: "var(--text-secondary)", borderRadius: 8, padding: 10, cursor: "pointer" }}>Cancel</button>
              <button onClick={() => handleRejectAbstract(abstractSelected)} disabled={processing} style={{ flex: 1, background: "#EF4444", border: "none", color: "#fff", borderRadius: 8, padding: 10, cursor: "pointer", fontWeight: 700 }}>
                {processing ? "Rejecting..." : "Reject Abstract"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}